import { ReactNode } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import type { PlatformUserRole } from "@/types";

interface RoleGateProps {
  /**
   * Required role (with hierarchy check)
   */
  role?: PlatformUserRole;
  /**
   * Any of these roles (OR logic, with hierarchy)
   */
  anyRole?: PlatformUserRole[];
  /**
   * Content to render when user has required role
   */
  children: ReactNode;
  /**
   * Optional fallback content when user lacks role
   * @default null (renders nothing)
   */
  fallback?: ReactNode;
}

/**
 * Component that conditionally renders children based on platform user role
 *
 * @example
 * // Single role
 * <RoleGate role="SUPER_ADMIN">
 *   <DeleteButton />
 * </RoleGate>
 *
 * @example
 * // Any role (OR)
 * <RoleGate anyRole={["SUPER_ADMIN", "SUPPORT_ADMIN"]}>
 *   <ManagementPanel />
 * </RoleGate>
 *
 * @example
 * // With fallback
 * <RoleGate role="SUPER_ADMIN" fallback={<p>Admin access required</p>}>
 *   <SystemSettings />
 * </RoleGate>
 */
export function RoleGate({
  role,
  anyRole,
  children,
  fallback = null,
}: RoleGateProps) {
  const { hasRole, hasAnyRole } = usePermissions();

  let hasAccess = true;

  // Check single role
  if (role) {
    if (!hasRole(role)) {
      hasAccess = false;
    }
  }

  // Check any role (OR)
  if (anyRole && anyRole.length > 0) {
    if (!hasAnyRole(anyRole)) {
      hasAccess = false;
    }
  }

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

/**
 * Higher-order component for role-based rendering
 *
 * @example
 * const AdminOnlyButton = withRole(Button, "SUPER_ADMIN");
 */
export function withRole<P extends object>(
  Component: React.ComponentType<P>,
  role: PlatformUserRole,
  fallback?: ReactNode,
) {
  return function WrappedComponent(props: P) {
    return (
      <RoleGate role={role} fallback={fallback}>
        <Component {...props} />
      </RoleGate>
    );
  };
}
